/**
 * Utilities for parsing and formatting human-readable duration strings.
 *
 * Supported formats:
 *   "30s"  → 30 seconds
 *   "5m"   → 300 seconds
 *   "2h"   → 7200 seconds
 *   "1d"   → 86400 seconds
 *   "1d12h" or "1d 12h 30m" → combined
 *   "300"  → 300 seconds (plain number fallback)
 */

const UNITS: Record<string, number> = {
  s: 1,
  m: 60,
  h: 3600,
  d: 86400,
};

const UNIT_NAMES: { unit: string; seconds: number; label: string }[] = [
  { unit: 'd', seconds: 86400, label: 'day' },
  { unit: 'h', seconds: 3600, label: 'hour' },
  { unit: 'm', seconds: 60, label: 'minute' },
  { unit: 's', seconds: 1, label: 'second' },
];

/**
 * Parse a human-readable duration string into total seconds.
 * Returns null if the input is invalid.
 *
 * Examples: "30s", "5m", "2h", "1d", "1d12h30m", "1d 12h 30m", "300"
 */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Plain number → treat as seconds
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  // Validate the entire input is composed of <number><unit> segments (with optional whitespace)
  if (!/^(\d+\s*[smhd]\s*)+$/i.test(trimmed)) return null;

  const regex = /(\d+)\s*([smhd])/gi;
  let total = 0;
  let match;

  while ((match = regex.exec(trimmed)) !== null) {
    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    total += value * UNITS[unit];
  }

  return total;
}

/**
 * Format seconds into a human-readable duration string.
 * Examples: 90 → "1m 30s", 86400 → "1d", 3661 → "1h 1m 1s"
 */
export function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0s';

  let remaining = totalSeconds;
  const parts: string[] = [];

  for (const { unit, seconds } of UNIT_NAMES) {
    if (remaining >= seconds) {
      const count = Math.floor(remaining / seconds);
      parts.push(`${count}${unit}`);
      remaining %= seconds;
    }
  }

  return parts.join(' ') || '0s';
}

/**
 * Format seconds into a long human-readable string for display.
 * Examples: 90 → "1 minute 30 seconds", 86400 → "1 day"
 */
export function formatDurationLong(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0 seconds';

  let remaining = totalSeconds;
  const parts: string[] = [];

  for (const { seconds, label } of UNIT_NAMES) {
    if (remaining >= seconds) {
      const count = Math.floor(remaining / seconds);
      parts.push(`${count} ${label}${count !== 1 ? 's' : ''}`);
      remaining %= seconds;
    }
  }

  return parts.join(' ') || '0 seconds';
}
